import { defineComponent as _defineComponent } from 'vue'
import { store } from '@/store';
import QueryParamService, { QueryParams } from '@/core/query-params.service';
import { PurchaseCtaEnum } from '@/services/product.service';
import Dropdown from '@/ui/shared/Dropdown.vue';
import PDPSwiper from './pdp-swiper/PDPSwiper.vue';
import ProductVariantSpec from './ProductVariantSpec.vue';
import GetCutsheetLink from './get-cutsheet/GetCutsheetLink.vue';
import TrackingService from '@/core/tracking.service';
import ProductPropertySelector from './ProductPropertySelector.vue';
import { initializePrivateFilter, PrivatePrioritizedVariantFilterObject, ProductProperty, ProductPropertyData, ProductPropertyOption } from './product-properties';
import { uniqBy } from 'lodash';
import useBreakpoints from '@/core/responsive/breakpoints/breakpoint.mixin';
import ProductViewInAugmentedReality from '@/ui/views/product-details/ProductViewInAugmentedReality.vue';
import { computed, ref, watch } from 'vue';

interface PrivateProductProperty {
    name: ProductProperty;
    description?: ProductProperty | undefined;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductVariantPicker',
  setup(__props) {

const product = computed<ProductBaseViewModel<VariantBaseViewObject>>(() => store.getters['catalog/product']);
const selectedVariant = computed<PrivateVariantViewObject>(() => store.getters['catalog/selectedVariant']);

const gaTrackingGoal = computed<string>(() => store.getters['catalog/gaTrackingGoal']);
const findRetailersUrl = computed<string>(() => store.getters['app/findRetailersUrl']);
const hasChangedRoute = computed<boolean>(() => store.getters['app/hasChangedRoute']);
const theme = computed<ColorTheme>(() => store.getters['app/theme']);

const breakpointIsActive = useBreakpoints();

function selectVariant(variant) {
    store.dispatch('catalog/selectVariant', variant);
}
function toggleShowVariantList(flag: boolean) {
    store.dispatch('catalog/toggleShowVariantList', flag);
}

const colors = ref<IColorOption[]>(getDistinctColors());
const options = ref<ProductPropertyData[]>([]);

const prioritizedVariantFilter = ref<PrivatePrioritizedVariantFilterObject>(initializePrivateFilter());

let hasTrackedVariant = false;
const productProperties: PrivateProductProperty[] = [{
    name: ProductProperty.propertyNameSizeText
},
{
    name: ProductProperty.propertyNameLightsourceText
},
{
    name: ProductProperty.propertyNameArmText
},
{
    name: ProductProperty.propertyNameMountingText
},
{
    name: ProductProperty.propertyNameCableType
},
{
    name: ProductProperty.propertyNameSwitchPlug
},
{
    name: ProductProperty.propertyNameShieldDiffusor
},
{
    name: ProductProperty.propertyNameAdditionalFeatures,
    description: ProductProperty.propertyNameAdditionalFeaturesDescription
}];

const hasSplashText = computed<boolean>(() => {
    return !!selectedVariant.value.splashText1 || !!selectedVariant.value.splashText2;
});

const getSplashIcon = computed<string>(() => {
    return selectedVariant.value.splash2Svg;
});

selectVariant(getVariantFromUrl() || getDefaultVariant());

watch(() => selectedVariant.value, onSelectedVariantChanged, { immediate: true });
function onSelectedVariantChanged(variant: VariantBaseViewObject) {
    prioritizedVariantFilter.value.sizeText.selectedValue = variant.sizeText;
    prioritizedVariantFilter.value.colorCode.selectedValue = variant.colorCode;
    prioritizedVariantFilter.value.lightsourceText.selectedValue = variant.lightsourceText;
    prioritizedVariantFilter.value.armText.selectedValue = variant.armText;
    prioritizedVariantFilter.value.mountingText.selectedValue = variant.mountingText;
    prioritizedVariantFilter.value.cableType.selectedValue = variant.cableType;
    prioritizedVariantFilter.value.switchPlug.selectedValue = variant.switchPlug;
    prioritizedVariantFilter.value.shieldDiffusor.selectedValue = variant.shieldDiffusor;
    prioritizedVariantFilter.value.additionalFeatures.selectedValue = variant.additionalFeatures;

    options.value = productProperties.map((property, index) => ({
        property: property.name,
        options: getDistinctVariantProperty(property.name, productProperties.map(y => y.name).slice(0, index), property.description)
    }));

    trackVariant();
}

const showDeliveryTime = computed<boolean>(() => {
    return selectedVariant.value.showDeliveryTime;
});

const showReadyForShipment = computed<boolean>(() => {
    return selectedVariant.value.showReadyForShipment;
});

const showAddToBasketButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.AddToBasket as any);
});

const showFindDealerButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.FindDealer as any);
});

const showGetQuoteButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.GetQuote as any);
});

const showDiscontinuedText = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.Discontinued as any);
});

const showSoldOutText = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.SoldOut as any) && !!selectedVariant.value.soldOutText;
});

const showPricingB2BButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.ShowPricingB2B as any);
});

const showAddToBasketB2BButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.AddToBasketB2B as any);
});

const showGetQuoteB2CButton = computed<boolean>(() => {
    return selectedVariant.value.purchaseCta === (PurchaseCtaEnum.GetQuoteB2C as any);
});

const productPackshotColor = computed<string>(() => {
    return !theme.value.productTileBackgroundColor ? product.value.backgroundColor : theme.value.productTileBackgroundColor;
});

function getDistinctColors() {
    return uniqBy(product.value.variants, v => v.colorCode).map(v => ({
        code: v.colorCode,
        image: v.colorImage,
        text: v.colorText
    }));
}

function validOptions(propToCheck: string, checkAgainst: ProductProperty[]) : string[] {
    return product.value.variants
        .filter(v => {
            let isValid = true;
            [ProductProperty.propertyNameColorCode].concat(checkAgainst).forEach(prop => {
                if (prop !== propToCheck && v[prop] !== selectedVariant.value[prop]) {
                    isValid = false;
                }
            });
            return isValid;
        })
        .map(v => v[propToCheck]);
}

function getDistinctVariantProperty(property: string, checkAgainst: ProductProperty[], descriptionProperty: string = undefined) : ProductPropertyOption[] {
    const props = uniqBy(product.value.variants.map(v => (
        {
            value: v[property] !== null ? v[property] : (Dropdown as any).EMPTY_VALUE,
            description: descriptionProperty ? v[descriptionProperty] : null
        })), o => o.value);

    const filteredProps = props.filter((prop) => {
        return prop.value.length > 0 && prop.value !== '-' && prop.value !== '--';
    });

    if (filteredProps.length === 1 && filteredProps[0].value === (Dropdown as any).EMPTY_VALUE) {
        return [];
    }

    const valid = validOptions(property, checkAgainst);
    return filteredProps.filter(option => valid.includes(option.value));
}

function getVariantIdFromUrl() : string {
    const id = QueryParamService.getParam(QueryParams.SelectedVariantId);
    return id;
}

function getVariantFromUrl() {
    // variant from url will always overrule variant.isDefault
    const id = getVariantIdFromUrl();
    const variant = product.value.variants.find(x => x.id === id);
    return variant;
}

function getDefaultVariant() {
    const defaultVariant = product.value.variants.find(v => v.isDefault);
    return defaultVariant || product.value.variants[0];
}

function findBestVariantCadidate(lastPropertyChanged: string, propertyValue: string) {
    const matchValue = (value: string, selected: string) => {
        const isMacthingEmpty = value === null && (selected === (Dropdown as any).EMPTY_VALUE || selected === '');
        const isMatchingValue = value === selected;
        return isMatchingValue ? true : isMacthingEmpty;
    };

    let candidates = product.value.variants.filter(v => matchValue(v[lastPropertyChanged], propertyValue));
    Object.keys(prioritizedVariantFilter.value).forEach(propertyName => {
        const entry = prioritizedVariantFilter.value[propertyName];
        if (entry.selectedValue) {
            const tempNextCandidates = candidates.filter(v => matchValue(v[propertyName], entry.selectedValue));
            candidates = tempNextCandidates.length > 0 ? tempNextCandidates : candidates;
        }
    });
    selectVariant(candidates[0]);
}

function changeFilter(value: string, property: string) {
    prioritizedVariantFilter.value[property].selectedValue = value;
    findBestVariantCadidate(property, value);
}

function showVariantList() {
    toggleShowVariantList(true);

    TrackingService.event('Ecommerce', gaTrackingGoal.value, 'Scroll to variant list - variant picker');
}

function trackVariant() {
    const useFirstPdpEventName = !hasChangedRoute.value && !hasTrackedVariant;

    if ((window as any).dataLayer) {
        const trackingProduct = {
            id: selectedVariant.value.sapId,
            price: (selectedVariant.value as PrivateVariantViewObject).rawPrice ? (selectedVariant.value as PrivateVariantViewObject).rawPrice : '',
            dimension3: getVariantIdFromUrl()
        };
        (window as any).dataLayer.push({
            event: useFirstPdpEventName ? 'firstpdp' : 'pdp',
            ecommerce: {
                currencyCode: product.value.productTracking.currencyCode ? product.value.productTracking.currencyCode : '',
                detail: {
                    products: [trackingProduct]
                }
            }
        });

        hasTrackedVariant = true;
    }
}

function findRetailerButtonClicked() {
    TrackingService.event('Ecommerce', gaTrackingGoal.value, 'Find dealer variant picker');
}

return { __sfc: true,product, selectedVariant, gaTrackingGoal, findRetailersUrl, hasChangedRoute, theme, breakpointIsActive, selectVariant, toggleShowVariantList, colors, options, prioritizedVariantFilter, hasTrackedVariant, productProperties, hasSplashText, getSplashIcon, onSelectedVariantChanged, showDeliveryTime, showReadyForShipment, showAddToBasketButton, showFindDealerButton, showGetQuoteButton, showDiscontinuedText, showSoldOutText, showPricingB2BButton, showAddToBasketB2BButton, showGetQuoteB2CButton, productPackshotColor, getDistinctColors, validOptions, getDistinctVariantProperty, getVariantIdFromUrl, getVariantFromUrl, getDefaultVariant, findBestVariantCadidate, changeFilter, showVariantList, trackVariant, findRetailerButtonClicked, PDPSwiper, ProductVariantSpec, GetCutsheetLink, ProductPropertySelector, ProductViewInAugmentedReality }
}

})